import React, { memo, useState, useMemo } from 'react';
import {
  Building,
  House,
  CaretDown,
  CaretRight,
  Question,
  ClipboardText,
  File,
  Robot,
  UserCircle,
  PencilSimple,
  ArrowSquareOut,
  CheckCircle,
  XCircle,
  Info,
  // Real Estate & Buildings
  HouseLine,
  HouseSimple,
  Buildings,
  BuildingOffice,
  Warehouse,
  Storefront,
  Bank,
  Factory,
  Hospital,
  Church,
  Garage,
  Door,
  Stairs,
  Elevator,
  // Infrastructure
  Blueprint,
  MapTrifold,
  MapPin,
  Compass,
  Path,
  // Technical & Utilities
  Fire,
  Lightning,
  Plug,
  Plugs,
  Drop,
  Pipe,
  Snowflake,
  Thermometer,
  Fan,
  Sun,
  SolarPanel,
  Wind,
  Lightbulb,
  Lamp,
  Broadcast,
  WifiHigh,
  // Safety & Security
  ShieldCheck,
  ShieldWarning,
  Shield,
  Lock,
  LockOpen,
  Key,
  Eye,
  Warning,
  Skull,
  Siren,
  FirstAid,
  Fingerprint,
  VideoCamera,
  // Tools & Maintenance
  Wrench,
  Hammer,
  Screwdriver,
  Toolbox,
  Gear,
  Faders,
  PaintRoller,
  Broom,
  // Nature & Environment
  Leaf,
  Tree,
  Plant,
  Flower,
  Recycle,
  Globe,
  Mountains,
  Waves,
  CloudRain,
  CloudSun,
  Umbrella,
  // Furniture & Interior
  Armchair,
  Bed,
  Bathtub,
  Toilet,
  Shower,
  CookingPot,
  // Measurements & Documents
  Ruler,
  Stack,
  Certificate,
  FileText as FileTextIcon,
  Notebook,
  Calculator,
  ChartLine,
  ChartBar,
  Scales,
  // Transport
  Car,
  Truck,
  Bicycle,
  Train,
  Airplane,
  Boat,
  // People & Communication
  Users,
  User,
  Handshake,
  Phone,
  Envelope,
  ChatCircle,
  Bell,
  Megaphone,
  // Time & Calendar
  Clock,
  Calendar,
  Hourglass,
  Timer,
  // Health & Medical
  Heartbeat,
  Heart,
  Pill,
  Stethoscope,
  Syringe,
  Bandaids,
  // Science & Education
  Atom,
  Flask,
  TestTube,
  Microscope,
  GraduationCap,
  BookOpen,
  // Business & Finance
  Money,
  CurrencyEur,
  CreditCard,
  Wallet,
  PiggyBank,
  Receipt,
  TrendUp,
  Percent,
  // Media & Entertainment
  Camera,
  MusicNote,
  Headphones,
  Television,
  GameController,
  // Misc
  Diamond,
  Star,
  Flag,
  Trophy,
  Target,
  Cube,
  TreeStructure,
  PuzzlePiece,
  Tag,
  Bookmark,
  Archive,
  Package,
  Gift,
  Barcode,
  QrCode,
  Check,
  X,
  ArrowsClockwise,
  IconProps,
} from '@phosphor-icons/react';
import { Tooltip } from '@heroui/react';
import { useDueDiligenceDotValues } from 'hooks/useDueDiligence';
import { useUsers } from 'shared/contexts/users.context';
import {
  DueDiligenceScope,
  CategoryDotValues,
  QuestionDotValue,
  DueDiligenceStatusColor,
  DueDiligenceResponseSourceType,
  responseSourceTypeLabels,
  SingleConditionResultAudit,
} from 'shared/types/due-diligence';
import { Skeleton } from '@/components/ui/skeleton';

// Icon map for category icons - comprehensive set matching settings page
const CATEGORY_ICON_MAP: Record<string, React.ComponentType<IconProps>> = {
  // Real Estate & Buildings
  House,
  HouseLine,
  HouseSimple,
  Building,
  Buildings,
  BuildingOffice,
  Warehouse,
  Storefront,
  Bank,
  Factory,
  Hospital,
  Church,
  Garage,
  Door,
  Stairs,
  Elevator,
  // Infrastructure
  Blueprint,
  MapTrifold,
  MapPin,
  Compass,
  Path,
  // Technical & Utilities
  Fire,
  Lightning,
  Plug,
  Plugs,
  Drop,
  Pipe,
  Snowflake,
  Thermometer,
  Fan,
  Sun,
  SolarPanel,
  Wind,
  Lightbulb,
  Lamp,
  Broadcast,
  WifiHigh,
  // Safety & Security
  ShieldCheck,
  ShieldWarning,
  Shield,
  Lock,
  LockOpen,
  Key,
  Eye,
  Warning,
  Skull,
  Siren,
  FirstAid,
  Fingerprint,
  VideoCamera,
  // Tools & Maintenance
  Wrench,
  Hammer,
  Screwdriver,
  Toolbox,
  Gear,
  Faders,
  PaintRoller,
  Broom,
  // Nature & Environment
  Leaf,
  Tree,
  Plant,
  Flower,
  Recycle,
  Globe,
  Mountains,
  Waves,
  CloudRain,
  CloudSun,
  Umbrella,
  // Furniture & Interior
  Armchair,
  Bed,
  Bathtub,
  Toilet,
  Shower,
  CookingPot,
  // Measurements & Documents
  Ruler,
  Stack,
  Certificate,
  FileText: FileTextIcon,
  Notebook,
  Calculator,
  ChartLine,
  ChartBar,
  Scales,
  ClipboardText,
  // Transport
  Car,
  Truck,
  Bicycle,
  Train,
  Airplane,
  Boat,
  // People & Communication
  Users,
  User,
  Handshake,
  Phone,
  Envelope,
  ChatCircle,
  Bell,
  Megaphone,
  // Time & Calendar
  Clock,
  Calendar,
  Hourglass,
  Timer,
  // Health & Medical
  Heartbeat,
  Heart,
  Pill,
  Stethoscope,
  Syringe,
  Bandaids,
  // Science & Education
  Atom,
  Flask,
  TestTube,
  Microscope,
  GraduationCap,
  BookOpen,
  // Business & Finance
  Money,
  CurrencyEur,
  CreditCard,
  Wallet,
  PiggyBank,
  Receipt,
  TrendUp,
  Percent,
  // Media & Entertainment
  Camera,
  MusicNote,
  Headphones,
  Television,
  GameController,
  // Misc
  Diamond,
  Star,
  Flag,
  Trophy,
  Target,
  Cube,
  TreeStructure,
  PuzzlePiece,
  Tag,
  Bookmark,
  Archive,
  Package,
  Gift,
  Barcode,
  QrCode,
  Check,
  X,
  Info,
};

/**
 * Get the icon component for a category
 */
const getCategoryIcon = (iconName?: string): React.ComponentType<IconProps> => {
  if (iconName && CATEGORY_ICON_MAP[iconName]) {
    return CATEGORY_ICON_MAP[iconName];
  }
  return ClipboardText; // Default icon
};

interface DueDiligenceEntityCardProps {
  buildingId?: string;
  unitId?: string;
  title: string;
  scope: DueDiligenceScope;
  /** @deprecated Category clicks now open detail page in new tab */
  onCategoryClick?: (category: CategoryDotValues) => void;
  onQuestionClick?: (category: CategoryDotValues, question: QuestionDotValue) => void;
}

/**
 * Infer the actual status color for a question.
 *
 * WORKAROUND: Backend dot-values endpoint has a bug where it returns 'transparent'
 * even when there's a valid response with a selectedOptionId. The modal correctly
 * shows the color because it fetches full response data and looks up the option's
 * statusColor. We need to infer the color from tooltipLabel patterns.
 */
const inferStatusColor = (q: QuestionDotValue): DueDiligenceStatusColor => {
  // Debug logging to help identify data issues
  if (q.statusColor === DueDiligenceStatusColor.TRANSPARENT) {
    console.log('[inferStatusColor] Transparent question:', {
      questionId: q.questionId,
      displayName: q.displayName,
      statusColor: q.statusColor,
      hasResponse: q.hasResponse,
      tooltipLabel: q.tooltipLabel,
      evaluationMode: q.evaluationMode,
      sourceFileLogId: q.sourceFileLogId,
      sourceFileName: q.sourceFileName,
      responseSourceType: q.responseSourceType,
    });
  }

  // If backend returned a non-transparent color, trust it
  if (q.statusColor !== DueDiligenceStatusColor.TRANSPARENT) {
    return q.statusColor;
  }

  // Backend bug workaround: If there's a response with tooltipLabel,
  // infer the color from common Dutch answer patterns
  if (q.hasResponse && q.tooltipLabel) {
    const label = q.tooltipLabel.toLowerCase().trim();

    // Green patterns (positive/compliant/present)
    const greenPatterns = [
      'aanwezig', 'ja', 'in orde', 'geldig', 'ok', 'goed', 'conform',
      'actief', 'goedgekeurd', 'voldoet', 'compliant', 'yes', 'present'
    ];
    if (greenPatterns.some(p => label === p || label.includes(p))) {
      console.log('[inferStatusColor] Inferred GREEN from tooltipLabel:', label);
      return DueDiligenceStatusColor.GREEN;
    }

    // Red patterns (negative/non-compliant/missing)
    const redPatterns = [
      'verlopen', 'nee', 'niet aanwezig', 'ontbreekt', 'niet conform',
      'ongeldig', 'afgekeurd', 'voldoet niet', 'non-compliant', 'no', 'missing', 'expired'
    ];
    if (redPatterns.some(p => label === p || label.includes(p))) {
      console.log('[inferStatusColor] Inferred RED from tooltipLabel:', label);
      return DueDiligenceStatusColor.RED;
    }

    // Orange patterns (attention needed)
    const orangePatterns = [
      'aandacht', 'waarschuwing', 'bijna verlopen', 'te controleren',
      'in behandeling', 'pending', 'warning'
    ];
    if (orangePatterns.some(p => label === p || label.includes(p))) {
      console.log('[inferStatusColor] Inferred ORANGE from tooltipLabel:', label);
      return DueDiligenceStatusColor.ORANGE;
    }
  }

  // Don't infer green just because a file is linked - the answer must be explicitly selected
  // Having a sourceFileLogId without a selected answer should remain transparent
  //
  // NOTE: We intentionally do NOT infer colors from tooltipLabel when hasResponse=false
  // because the backend sets tooltipLabel to the question's displayName when there's no response.
  // This would cause false positives (e.g., "Verkoopakte bouwgrond aanwezig?" contains "aanwezig"
  // which would incorrectly infer GREEN even when there's no actual response).

  return q.statusColor;
};

/**
 * Calculate aggregated dot values for a category
 */
const calculateAggregatedDots = (questions: QuestionDotValue[]) => {
  const result = {
    green: { count: 0, labels: [] as string[] },
    orange: { count: 0, labels: [] as string[] },
    red: { count: 0, labels: [] as string[] },
    transparent: { count: 0, labels: [] as string[] },
  };

  for (const q of questions) {
    const color = inferStatusColor(q) as keyof typeof result;
    if (result[color]) {
      result[color].count++;
      // Show "Question: Answer" format so users understand why the dot is this color
      const label = q.tooltipLabel
        ? `${q.displayName}: ${q.tooltipLabel}`
        : q.displayName;
      result[color].labels.push(label);
    }
  }

  return result;
};

/**
 * Colored dot component for status indicators - matches old DD styling
 */
const StatusDot: React.FC<{
  color: 'green' | 'orange' | 'red' | 'transparent';
  count: number;
  labels: string[];
}> = ({ color, count, labels }) => {
  // Base colors (muted)
  const colorClasses = {
    green: 'border-status-good bg-status-good/10',
    orange: 'border-status-medium bg-status-medium/10',
    red: 'border-status-bad bg-status-bad/10',
    transparent: 'border-neutral-primary/30 bg-transparent',
  };

  // Enhanced colors when count > 0 (solid fill)
  const enhancedColorClasses = {
    green: 'bg-status-good border-status-good',
    orange: 'bg-status-medium border-status-medium',
    red: 'bg-status-bad border-status-bad',
    transparent: 'border-neutral-primary/30 bg-transparent',
  };

  // Text colors when count > 0
  const textColorClasses = {
    green: 'font-medium text-status-good',
    orange: 'font-medium text-status-medium',
    red: 'font-medium text-status-bad',
    transparent: 'text-neutral-secondary-mid',
  };

  const dotColorStyle = count > 0 ? enhancedColorClasses[color] : colorClasses[color];
  const textColorStyle = count > 0 ? textColorClasses[color] : 'text-neutral-secondary-mid';

  return (
    <div className="flex items-center justify-center gap-1.5">
      <Tooltip
        content={labels.length > 0 ? labels.join(', ') : 'Geen items'}
        containerPadding={50}
        classNames={{
          base: 'max-w-[700px]',
          content: 'max-h-[200px] top-0 overflow-y-scroll items-start justify-start p-3 font-normal leading-normal',
        }}
      >
        <div className={`h-4 w-4 rounded-full border ${dotColorStyle}`} />
      </Tooltip>
      <span className={`w-5 text-[13px] ${textColorStyle}`}>
        {count}
      </span>
    </div>
  );
};

/**
 * Renders a single condition result for the audit trail
 */
const ConditionResultRow: React.FC<{ result: SingleConditionResultAudit; depth?: number }> = ({ result, depth = 0 }) => {
  const [showAiDetails, setShowAiDetails] = useState(false);
  const paddingLeft = depth * 16;

  return (
    <div style={{ paddingLeft }} className="py-1">
      <div className="flex items-start gap-2">
        {result.passed ? (
          <CheckCircle weight="fill" size={14} className="text-status-good mt-0.5 flex-shrink-0" />
        ) : (
          <XCircle weight="fill" size={14} className="text-status-bad mt-0.5 flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <p className="text-xs text-neutral-secondary-dark">{result.description}</p>
          {result.details?.message && (
            <p className="text-xs text-neutral-secondary-mid mt-0.5">{result.details.message}</p>
          )}
          {result.details?.foundValue && (
            <p className="text-xs text-neutral-secondary-mid mt-0.5">
              Gevonden: <span className="font-mono">{result.details.foundValue}</span>
            </p>
          )}
          {/* AI Details toggle */}
          {result.aiDetails && (
            <div className="mt-1">
              <button
                onClick={(e) => { e.stopPropagation(); setShowAiDetails(!showAiDetails); }}
                className="text-xs text-blue-600 hover:underline flex items-center gap-1"
              >
                <Info size={12} />
                {showAiDetails ? 'Verberg AI details' : 'Toon AI details'}
              </button>
              {showAiDetails && (
                <div className="mt-2 p-2 bg-blue-50 rounded text-xs space-y-2">
                  <div>
                    <p className="font-medium text-blue-800">Prompt:</p>
                    <pre className="whitespace-pre-wrap text-blue-700 font-mono text-[10px] mt-1">{result.aiDetails.promptUsed}</pre>
                  </div>
                  <div>
                    <p className="font-medium text-blue-800">AI Antwoord ({result.aiDetails.model}):</p>
                    <pre className="whitespace-pre-wrap text-blue-700 font-mono text-[10px] mt-1">{result.aiDetails.rawResponse}</pre>
                  </div>
                  {result.aiDetails.interpretedAs && (
                    <p className="text-blue-800">
                      Geïnterpreteerd als: <span className="font-medium">{result.aiDetails.interpretedAs}</span>
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {/* Nested children for groups */}
      {result.children && result.children.length > 0 && (
        <div className="mt-1 border-l-2 border-neutral-primary/20 ml-1.5">
          {result.children.map((child, idx) => (
            <ConditionResultRow key={child.conditionId || idx} result={child} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * DueDiligenceEntityCard displays configurable due diligence categories and their status dots.
 * Uses moss green styling to match the new design language.
 */
const DueDiligenceEntityCard: React.FC<DueDiligenceEntityCardProps> = ({
  buildingId,
  unitId,
  title,
  scope,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onCategoryClick: _onCategoryClick, // Deprecated - category clicks now open detail page in new tab
  onQuestionClick,
}) => {
  const { dotValues, isLoading, error } = useDueDiligenceDotValues(
    buildingId,
    unitId
  );
  const { users } = useUsers();
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(new Set());
  const [expandedQuestionAudit, setExpandedQuestionAudit] = useState<number | null>(null);

  // Helper to get user name from clerk userId
  const getUserName = useMemo(() => (userId: string | undefined) => {
    if (!userId) return null;
    const user = users.find((u) => u.id === userId);
    return user ? `${user.first_name} ${user.last_name}` : userId;
  }, [users]);

  // Filter categories by scope
  const filteredCategories = useMemo(
    () => dotValues.filter(cat => cat.scope === scope),
    [dotValues, scope]
  );

  // Debug logging
  console.log('[DueDiligenceEntityCard] Dot values loaded:', {
    buildingId,
    unitId,
    scope,
    totalDotValues: dotValues.length,
    dotValues: dotValues.map(d => ({ id: d.categoryId, name: d.categoryName, scope: d.scope })),
    filteredCategories: filteredCategories.length,
    isLoading,
    error,
  });

  const toggleCategory = (categoryId: number) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  // Determine border color based on scope - buildings get moss green, units get neutral
  const borderClass = scope === DueDiligenceScope.BUILDING
    ? 'border-2 border-moss-primary'
    : 'border border-neutral-primary/30';

  if (isLoading) {
    return (
      <div className={`relative overflow-hidden rounded-lg ${borderClass} bg-white text-sm shadow-sm`}>
        <div className="relative z-1 flex flex-col divide-y divide-neutral-primary/20">
          <header className="flex items-center gap-1.5 px-3 py-2.5">
            {scope === DueDiligenceScope.BUILDING ? (
              <Building weight="duotone" size="18" className="text-moss-primary" />
            ) : (
              <House weight="duotone" size="18" className="text-neutral-secondary-mid" />
            )}
            <Skeleton className="h-5 w-48" />
          </header>
          <div className="p-3 space-y-3">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`relative overflow-hidden rounded-lg ${borderClass} bg-white text-sm shadow-sm`}>
        <div className="p-4 text-center text-muted-foreground">
          <p>Kon due diligence gegevens niet laden</p>
        </div>
      </div>
    );
  }

  if (filteredCategories.length === 0) {
    return (
      <div className="relative overflow-hidden rounded-lg border-2 border-dashed border-neutral-primary/30 bg-white text-sm shadow-sm">
        <div className="p-6 text-center text-muted-foreground">
          <ClipboardText weight="duotone" size={32} className="mx-auto mb-2 text-neutral-primary/30" />
          <p className="text-sm">Geen categorieën geconfigureerd</p>
          <p className="text-xs mt-1">Ga naar Instellingen om categorieën toe te voegen</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative overflow-hidden rounded-lg ${borderClass} bg-white text-sm shadow-sm`}>
      <div className="flex flex-col divide-y divide-neutral-primary/20">
        {/* Header */}
        <header className="flex items-center gap-1.5 px-3 py-2.5">
          {scope === DueDiligenceScope.BUILDING ? (
            <Building weight="duotone" size="18" className="text-moss-primary" />
          ) : (
            <House weight="duotone" size="18" className="text-neutral-secondary-mid" />
          )}
          <h3 className="font-extrabold text-lg text-gray-900">
            {title}
          </h3>
        </header>

        {/* Categories */}
        <ul className="flex flex-col divide-y divide-neutral-primary/10">
          {filteredCategories.map((category) => {
            const isExpanded = expandedCategories.has(category.categoryId);
            const aggregatedDots = calculateAggregatedDots(category.questions);
            const hasQuestions = category.questions.length > 0;

            return (
              <li key={category.categoryId} className="divide-y divide-neutral-primary/10">
                {/* Category Row - opens detail page in new tab */}
                <div
                  className="flex cursor-pointer flex-wrap items-center justify-between gap-3 px-3 py-2.5 transition-all hover:bg-neutral-primary/5"
                  onClick={() => {
                    // Open the DD category detail page in a new tab
                    const url = unitId
                      ? `/building/${buildingId}/unit/${unitId}/due-diligence/${category.categoryId}`
                      : `/building/${buildingId}/due-diligence/${category.categoryId}`;
                    window.open(url, '_blank');
                  }}
                >
                  <div className="flex items-center gap-2">
                    {hasQuestions && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleCategory(category.categoryId);
                        }}
                        className="p-0.5 hover:bg-neutral-primary/10 rounded"
                      >
                        {isExpanded ? (
                          <CaretDown weight="bold" size={14} className="text-moss-primary" />
                        ) : (
                          <CaretRight weight="bold" size={14} className="text-moss-primary" />
                        )}
                      </button>
                    )}
                    {React.createElement(getCategoryIcon(category.categoryIcon), {
                      weight: 'duotone',
                      size: 18,
                      className: 'text-moss-primary',
                    })}
                    <span>{category.categoryName}</span>
                  </div>
                  {/* Aggregated dot counts */}
                  <div className="flex cursor-pointer items-center gap-1.5">
                    <StatusDot
                      color="green"
                      count={aggregatedDots.green.count}
                      labels={aggregatedDots.green.labels}
                    />
                    <StatusDot
                      color="orange"
                      count={aggregatedDots.orange.count}
                      labels={aggregatedDots.orange.labels}
                    />
                    <StatusDot
                      color="red"
                      count={aggregatedDots.red.count}
                      labels={aggregatedDots.red.labels}
                    />
                    <StatusDot
                      color="transparent"
                      count={aggregatedDots.transparent.count}
                      labels={aggregatedDots.transparent.labels}
                    />
                  </div>
                </div>

                {/* Expanded Questions */}
                {isExpanded && hasQuestions && (
                  <ul className="flex flex-col divide-y divide-neutral-primary/5 bg-neutral-primary/5">
                    {category.questions.map((question) => {
                      const isFileBased = question.evaluationMode === 'file_based';
                      const fileInfo = isFileBased && question.sourceFileName
                        ? `Bron: ${question.sourceFileName}${question.sourceFileLabel ? ` (${question.sourceFileLabel})` : ''}`
                        : null;
                      const inferredColor = inferStatusColor(question);

                      // Build audit trail tooltip
                      const auditLines: string[] = [];
                      if (question.responseSourceType) {
                        auditLines.push(`Invoer: ${responseSourceTypeLabels[question.responseSourceType] || question.responseSourceType}`);
                      }
                      if (question.aiConfidence !== undefined) {
                        auditLines.push(`AI zekerheid: ${Math.round(question.aiConfidence * 100)}%`);
                      }
                      if (question.updatedBy && question.updatedAt) {
                        const date = new Date(question.updatedAt).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' });
                        auditLines.push(`Laatst gewijzigd: ${date}`);
                      }

                      // Show "Question: Answer" format so users understand why the dot is this color
                      const answerLine = question.tooltipLabel
                        ? `${question.displayName}: ${question.tooltipLabel}`
                        : question.displayName;
                      const fullTooltip = [
                        answerLine,
                        fileInfo,
                        auditLines.length > 0 ? `\n--- Audit ---\n${auditLines.join('\n')}` : null,
                      ].filter(Boolean).join('\n');

                      // Determine source icon for audit trail with tooltip
                      const getSourceIcon = () => {
                        if (question.responseSourceType === DueDiligenceResponseSourceType.AI_EXTRACTED) {
                          return (
                            <Tooltip content="AI geëxtraheerd (dropzone)">
                              <span><Robot weight="duotone" size={12} className="text-blue-500" /></span>
                            </Tooltip>
                          );
                        }
                        if (question.responseSourceType === DueDiligenceResponseSourceType.AI_VALIDATED) {
                          return (
                            <Tooltip content="AI geëxtraheerd, gevalideerd door gebruiker">
                              <span><Robot weight="duotone" size={12} className="text-green-500" /></span>
                            </Tooltip>
                          );
                        }
                        if (question.responseSourceType === DueDiligenceResponseSourceType.AI_EDITED) {
                          return (
                            <Tooltip content="AI geëxtraheerd, bewerkt door gebruiker">
                              <span><PencilSimple weight="duotone" size={12} className="text-orange-500" /></span>
                            </Tooltip>
                          );
                        }
                        if (question.responseSourceType === DueDiligenceResponseSourceType.SYSTEM_REEVALUATION) {
                          return (
                            <Tooltip content="Automatisch geherwaardeerd">
                              <span><ArrowsClockwise weight="duotone" size={12} className="text-purple-500" /></span>
                            </Tooltip>
                          );
                        }
                        if (question.responseSourceType === DueDiligenceResponseSourceType.MANUAL) {
                          return (
                            <Tooltip content="Handmatig ingevoerd">
                              <span><UserCircle weight="duotone" size={12} className="text-neutral-secondary-mid" /></span>
                            </Tooltip>
                          );
                        }
                        return null;
                      };
                      const sourceIcon = getSourceIcon();

                      const hasAuditDetails = (question.conditionEvaluations?.optionEvaluations?.length ?? 0) > 0;
                      const isAuditExpanded = expandedQuestionAudit === question.questionId;

                      return (
                        <li key={question.questionId} className="flex flex-col">
                          {/* Question row */}
                          <div
                            className="flex cursor-pointer flex-wrap items-center justify-between gap-3 px-3 py-2 pl-12 transition-all hover:bg-neutral-primary/10"
                            onClick={() => onQuestionClick?.(category, question)}
                            title={fullTooltip}
                          >
                            <div className="flex items-center gap-2">
                              {isFileBased ? (
                                <File weight="duotone" size={14} className="text-moss-primary" />
                              ) : (
                                <Question weight="duotone" size={14} className="text-neutral-secondary-mid" />
                              )}
                              <span className="text-sm text-neutral-secondary-dark">
                                {question.displayName}
                              </span>
                              {/* Show the answer value if different from the question name */}
                              {question.hasResponse && question.tooltipLabel && question.tooltipLabel !== question.displayName && (
                                <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                                  inferredColor === DueDiligenceStatusColor.GREEN
                                    ? 'bg-status-good/10 text-status-good'
                                    : inferredColor === DueDiligenceStatusColor.ORANGE
                                    ? 'bg-status-medium/10 text-status-medium'
                                    : inferredColor === DueDiligenceStatusColor.RED
                                    ? 'bg-status-bad/10 text-status-bad'
                                    : 'bg-neutral-100 text-neutral-secondary-mid'
                                }`}>
                                  {question.tooltipLabel}
                                </span>
                              )}
                              {isFileBased && question.sourceFileName && (
                                <span className="text-xs text-neutral-secondary-mid truncate max-w-[200px]">
                                  ({question.sourceFileName})
                                </span>
                              )}
                              {/* Audit trail source icon */}
                              {sourceIcon}
                              {/* Toggle audit details button */}
                              {hasAuditDetails && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedQuestionAudit(isAuditExpanded ? null : question.questionId);
                                  }}
                                  className="ml-1 text-xs text-blue-600 hover:underline"
                                >
                                  {isAuditExpanded ? 'Verberg' : 'Details'}
                                </button>
                              )}
                            </div>
                            <div
                              className={`h-3.5 w-3.5 rounded-full border ${
                                inferredColor === DueDiligenceStatusColor.GREEN
                                  ? 'bg-status-good border-status-good'
                                  : inferredColor === DueDiligenceStatusColor.ORANGE
                                  ? 'bg-status-medium border-status-medium'
                                  : inferredColor === DueDiligenceStatusColor.RED
                                  ? 'bg-status-bad border-status-bad'
                                  : 'bg-transparent border-neutral-primary/30'
                              }`}
                            />
                          </div>

                          {/* Expanded audit trail */}
                          {isAuditExpanded && question.conditionEvaluations && (
                            <div className="px-3 py-2 pl-16 bg-neutral-50 border-t border-neutral-primary/10">
                              {/* Validator info for AI results */}
                              {(question.responseSourceType === DueDiligenceResponseSourceType.AI_EXTRACTED ||
                                question.responseSourceType === DueDiligenceResponseSourceType.AI_VALIDATED ||
                                question.responseSourceType === DueDiligenceResponseSourceType.AI_EDITED) &&
                                question.validatedBy && (
                                <p className="text-xs text-green-600 mb-2">
                                  <CheckCircle weight="fill" size={12} className="inline mr-1" />
                                  Gevalideerd door {getUserName(question.validatedBy)}
                                  {question.validatedAt && (
                                    <span className="text-neutral-secondary-mid ml-1">
                                      op {new Date(question.validatedAt).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' })}
                                    </span>
                                  )}
                                </p>
                              )}
                              {/* Source document link */}
                              {question.sourceFileLogId && (
                                <p className="text-xs text-neutral-secondary-mid mb-2 flex items-center gap-1">
                                  <File weight="duotone" size={12} className="text-blue-500" />
                                  <span>Bron: </span>
                                  {question.sourceFileSharePointUrl ? (
                                    <a
                                      href={question.sourceFileSharePointUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-blue-600 hover:underline inline-flex items-center gap-0.5"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {question.sourceFileLabel || question.sourceFileName || `Document #${question.sourceFileLogId}`}
                                      <ArrowSquareOut size={10} />
                                    </a>
                                  ) : (
                                    <span>{question.sourceFileLabel || question.sourceFileName || `Document #${question.sourceFileLogId}`}</span>
                                  )}
                                  <span className="text-neutral-secondary-mid/60">(ID: {question.sourceFileLogId})</span>
                                </p>
                              )}
                              {question.conditionEvaluations.summary && (
                                <p className="text-xs text-neutral-secondary-dark mb-2 font-medium">
                                  {question.conditionEvaluations.summary}
                                </p>
                              )}
                              {question.conditionEvaluations.optionEvaluations?.map((optionEval: any) => (
                                <div key={optionEval.optionId} className="mb-3 last:mb-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    {optionEval.matched ? (
                                      <CheckCircle weight="fill" size={14} className="text-status-good" />
                                    ) : (
                                      <XCircle weight="fill" size={14} className="text-neutral-secondary-mid" />
                                    )}
                                    <span className={`text-xs font-medium ${optionEval.matched ? 'text-status-good' : 'text-neutral-secondary-mid'}`}>
                                      {optionEval.optionLabel}
                                    </span>
                                    {optionEval.matched && (
                                      <span className="text-xs bg-status-good/10 text-status-good px-1.5 py-0.5 rounded">
                                        Geselecteerd
                                      </span>
                                    )}
                                  </div>
                                  {optionEval.conditionResults?.map((result: SingleConditionResultAudit, idx: number) => (
                                    <ConditionResultRow key={result.conditionId || idx} result={result} />
                                  ))}
                                </div>
                              ))}
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default memo(DueDiligenceEntityCard);
